import { isEqual } from 'lodash';
import { ParameterError } from '../types/errors';
import { ChartType } from '../types/savedCharts';
export const DOCUMENT_CHART_TAG = 'document-chart';
const TAG_LINE_RE = /^<([a-z][a-z-]*)((?:\s+[a-z][a-z-]*="[^"]*")*)\s*\/?>(?:<\/\1>)?$/;
const ATTRIBUTE_RE = /([a-z][a-z-]*)="([^"]*)"/g;
const FENCE_RE = /^(`{3,}|~{3,})/;
const DOCUMENT_CHART_ID_RE = /^c([1-9]\d*)$/;
const decodeAttribute = (value) => value
    .replaceAll('&quot;', '"')
    .replaceAll('&lt;', '<')
    .replaceAll('&gt;', '>')
    .replaceAll('&amp;', '&');
const encodeAttribute = (value) => value
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
/** Drop surrounding blank lines, keeping the first line's indentation. */
const trimBlankLines = (lines) => {
    const start = lines.findIndex((line) => line.trim() !== '');
    if (start === -1)
        return '';
    const end = lines.findLastIndex((line) => line.trim() !== '');
    return lines
        .slice(start, end + 1)
        .join('\n')
        .trimEnd();
};
export const parseDocumentBlocks = (markdown, tagNames) => {
    const blocks = [];
    let lines = [];
    let fence = null;
    const flush = () => {
        const text = trimBlankLines(lines);
        if (text)
            blocks.push({ type: 'markdown', markdown: text });
        lines = [];
    };
    markdown.split(/\r?\n/).forEach((line) => {
        const fenceMatch = FENCE_RE.exec(line.trim());
        if (fence !== null) {
            if (fenceMatch && fenceMatch[1].startsWith(fence))
                fence = null;
            lines.push(line);
            return;
        }
        if (fenceMatch) {
            [, fence] = fenceMatch;
            lines.push(line);
            return;
        }
        const tagMatch = TAG_LINE_RE.exec(line.trimEnd());
        if (tagMatch && tagNames.includes(tagMatch[1])) {
            flush();
            blocks.push({
                type: 'tag',
                tag: {
                    name: tagMatch[1],
                    attributes: Object.fromEntries([...tagMatch[2].matchAll(ATTRIBUTE_RE)].map(([, key, value]) => [key, decodeAttribute(value)])),
                },
            });
            return;
        }
        lines.push(line);
    });
    flush();
    return blocks;
};
export const formatDocumentTag = ({ name, attributes }) => `<${name}${Object.entries(attributes)
    .map(([key, value]) => ` ${key}="${encodeAttribute(value)}"`)
    .join('')}>`;
export const joinDocumentBlocks = (blocks) => blocks
    .map((block) => block.type === 'markdown'
    ? block.markdown
    : formatDocumentTag(block.tag))
    .join('\n\n');
export const getDocumentChartTag = (id) => formatDocumentTag({ name: DOCUMENT_CHART_TAG, attributes: { id } });
/** The number of a stored chart id (`c3` → 3), or undefined for a temporary key. */
export const getDocumentChartNumber = (id) => {
    const match = DOCUMENT_CHART_ID_RE.exec(id);
    return match ? Number(match[1]) : undefined;
};
/**
 * The Document in reading order. Chart tags must reference an entry in
 * `charts`, once each; other tags stay markdown.
 */
export const getDocumentChartBlocks = (content) => {
    const seen = new Set();
    return parseDocumentBlocks(content.markdown, [DOCUMENT_CHART_TAG]).map((block) => {
        if (block.type === 'markdown')
            return block;
        const { id } = block.tag.attributes;
        if (!id) {
            throw new ParameterError(`Every <${DOCUMENT_CHART_TAG}> tag needs an id`);
        }
        if (seen.has(id)) {
            throw new ParameterError(`Chart "${id}" is placed more than once`);
        }
        seen.add(id);
        const chart = Object.hasOwn(content.charts, id)
            ? content.charts[id]
            : undefined;
        if (chart === undefined) {
            throw new ParameterError(`Chart "${id}" is placed in the markdown but missing from charts`);
        }
        return { type: 'chart', id, chart };
    });
};
/** Canonical content: chart tags carry only their id, unplaced charts are dropped. */
export const fromDocumentChartBlocks = (blocks) => ({
    markdown: joinDocumentBlocks(blocks.map((block) => block.type === 'markdown'
        ? block
        : {
            type: 'tag',
            tag: {
                name: DOCUMENT_CHART_TAG,
                attributes: { id: block.id },
            },
        })),
    charts: Object.fromEntries(blocks.flatMap((block) => block.type === 'chart' ? [[block.id, block.chart]] : [])),
});
export const mapDocumentCharts = (content, map) => ({
    ...content,
    charts: Object.fromEntries(Object.entries(content.charts).map(([id, chart]) => [
        id,
        map(chart, id),
    ])),
});
/**
 * Replace temporary chart keys with stored ids. Keys already shaped like
 * `c<n>` are kept; the others get the next free numbers in reading order, so
 * an id is never handed out twice for one Document.
 */
export const assignDocumentChartIds = (content, nextChartNumber) => {
    const blocks = getDocumentChartBlocks(content);
    let next = Math.max(nextChartNumber, ...blocks.map((block) => block.type === 'chart'
        ? (getDocumentChartNumber(block.id) ?? 0) + 1
        : 1));
    const assigned = blocks.map((block) => {
        if (block.type === 'markdown' ||
            getDocumentChartNumber(block.id) !== undefined) {
            return block;
        }
        const id = `c${next}`;
        next += 1;
        return { ...block, id };
    });
    return {
        content: fromDocumentChartBlocks(assigned),
        nextChartNumber: next,
    };
};
export const getDocumentChartList = (content) => getDocumentChartBlocks(content).flatMap((block) => block.type === 'chart' ? [{ id: block.id, chart: block.chart }] : []);
const getChartTypeLabel = ({ chart }) => {
    if (chart.chartConfig.type !== ChartType.CARTESIAN) {
        return chart.chartConfig.type;
    }
    return chart.chartConfig.config?.eChartsConfig.series?.[0]?.type ?? 'bar';
};
/**
 * The Document with each chart tag expanded into a short description (id,
 * title, type, explore), for readers that should not receive full charts.
 */
export const getDocumentSummaryMarkdown = (content) => joinDocumentBlocks(getDocumentChartBlocks(content).map((block) => {
    if (block.type === 'markdown')
        return block;
    const { chart, source } = block.chart;
    return {
        type: 'tag',
        tag: {
            name: DOCUMENT_CHART_TAG,
            attributes: {
                id: block.id,
                title: chart.name,
                type: getChartTypeLabel(block.chart),
                explore: chart.tableName,
                ...(source === 'merge' ? { source } : {}),
            },
        },
    };
}));
/** Tags an AI agent writes to place charts from its conversation. */
export const ARTIFACT_CHART_TAG = 'artifact-chart';
export const QUERY_RESULT_TAG = 'query-result';
export const DOCUMENT_CONVERSATION_TAGS = [
    ARTIFACT_CHART_TAG,
    QUERY_RESULT_TAG,
];
/**
 * Give a temporary chart key the stored id of an identical, otherwise unplaced
 * chart in the previous version, so re-sending an unchanged chart under a
 * temporary key (e.g. re-uploading a file) does not copy it.
 */
export const matchDocumentChartKeys = (content, previous) => {
    const blocks = getDocumentChartBlocks(content);
    const used = new Set(blocks.flatMap((block) => block.type === 'chart' &&
        getDocumentChartNumber(block.id) !== undefined
        ? [block.id]
        : []));
    return fromDocumentChartBlocks(blocks.map((block) => {
        if (block.type === 'markdown' ||
            getDocumentChartNumber(block.id) !== undefined) {
            return block;
        }
        const match = Object.entries(previous.charts).find(([id, chart]) => !used.has(id) && isEqual(chart, block.chart));
        if (!match)
            return block;
        used.add(match[0]);
        return { ...block, id: match[0] };
    }));
};
//# sourceMappingURL=documentMarkdown.js.map